if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.initializeExplicitSizeSelection();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (this.isSizeOption(target)) this.markExplicitSizeSelection(target);
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          target,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      requiresExplicitSizeSelection() {
        return this.productForm?.dataset.zicoRequiresSize === 'true';
      }

      hasExplicitSizeSelection() {
        return !this.requiresExplicitSizeSelection() || this.productForm?.dataset.zicoSizeSelected === 'true';
      }

      isSizeOption(option) {
        if (!option) return false;
        if (option.dataset.zicoSizeOption === 'true') return true;

        const optionName = (option.dataset.optionName || '').trim().toLocaleLowerCase();
        return optionName.includes('size') || optionName.includes('ขนาด');
      }

      markExplicitSizeSelection(option) {
        const productForm = this.productForm;
        if (!productForm || !this.requiresExplicitSizeSelection()) return;

        productForm.dataset.zicoSizeSelected = 'true';
        if (option?.dataset.optionValueId) {
          productForm.dataset.zicoSizeValueId = option.dataset.optionValueId;
        }
      }

      initializeExplicitSizeSelection() {
        if (!this.requiresExplicitSizeSelection()) return;

        if (!this.hasExplicitSizeSelection()) this.clearImplicitSizeSelection();
        this.enforceExplicitSizeSelection();
      }

      clearImplicitSizeSelection() {
        if (!this.requiresExplicitSizeSelection()) return;

        const label = this.productForm?.dataset.zicoSelectSizeLabel || 'SELECT SIZE';
        this.querySelectorAll('[data-zico-size-option="true"]').forEach((option) => {
          if (option.tagName === 'INPUT') {
            option.checked = false;
            option.removeAttribute('checked');
          } else if (option.tagName === 'OPTION') {
            option.selected = false;
            option.removeAttribute('selected');
          }
        });

        this.querySelectorAll('select[data-zico-size-option-select="true"]').forEach((select) => {
          let placeholder = select.querySelector('[data-zico-size-placeholder]');
          if (!placeholder) {
            placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.disabled = true;
            placeholder.textContent = label;
            placeholder.dataset.zicoSizePlaceholder = 'true';
            select.prepend(placeholder);
          }
          placeholder.selected = true;
          select.value = '';
        });

        this.querySelectorAll('[data-zico-size-option-group="true"] [data-selected-value]').forEach((value) => {
          value.textContent = label;
        });
      }

      restoreExplicitSizeSelection() {
        const valueId = this.productForm?.dataset.zicoSizeValueId;
        if (!valueId) return;

        const selectedOption = Array.from(this.querySelectorAll('[data-zico-size-option="true"]')).find(
          (option) => option.dataset.optionValueId === valueId
        );
        if (!selectedOption) return;

        if (selectedOption.tagName === 'INPUT') {
          selectedOption.checked = true;
        } else if (selectedOption.tagName === 'OPTION') {
          const select = selectedOption.closest('select');
          select?.querySelectorAll('option[selected]').forEach((option) => option.removeAttribute('selected'));
          selectedOption.selected = true;
          selectedOption.setAttribute('selected', 'selected');
        }

        const selectedValue = selectedOption.value;
        selectedOption
          .closest('[data-zico-size-option-group="true"]')
          ?.querySelectorAll('[data-selected-value]')
          .forEach((value) => {
            value.textContent = selectedValue;
          });
      }

      enforceExplicitSizeSelection() {
        if (!this.requiresExplicitSizeSelection()) return true;

        const productForm = this.productForm;
        const isSelected = this.hasExplicitSizeSelection();
        if (isSelected) {
          this.restoreExplicitSizeSelection();
          return true;
        }

        this.clearImplicitSizeSelection();
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (!input || input.value === '') return;
          input.value = '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const label = productForm?.dataset.zicoSelectSizeLabel || 'SELECT SIZE';
        if (typeof productForm?.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(true, label);
        } else {
          const button = productForm?.querySelector('[type="submit"]');
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
            const text = button.querySelector('span');
            if (text) text.textContent = label;
          }
        }
        return false;
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html, request) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const sourceVariantSelects = this.getVariantSelects(sourceProductInfo);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.resolveRequestDeferred(request, variant, sourceVariantSelects);
          this.prepareSizeChartEntry(sourceVariantSelects, sourceProductInfo);
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
              { replaceInPlace: true }
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
              { replaceInPlace: true }
            );
          }
        };
      }

      createAbortError() {
        const error = new Error('Fetch aborted by user');
        error.name = 'AbortError';
        return error;
      }

      isCurrentRequest(request) {
        return this.isConnected && this.abortController === request.controller && !request.controller.signal.aborted;
      }

      resolveRequestDeferred(request, variant, sourceVariantSelects) {
        if (!request?.deferred || request.deferredSettled) return;

        request.deferredSettled = true;
        if (request.variantSelectors?.pendingSelectPromise === request.deferred) {
          request.variantSelectors.resolvePendingSelectPromise(variant, sourceVariantSelects);
        }
      }

      rejectRequestDeferred(request, error) {
        if (!request?.deferred || request.deferredSettled) return;

        request.deferredSettled = true;
        if (request.variantSelectors?.pendingSelectPromise === request.deferred) {
          request.variantSelectors.rejectPendingSelectPromise(error);
        } else {
          request.deferred.reject(error);
        }
      }

      hasRequestFocus(request) {
        return Boolean(request.target && this.contains(request.target) && document.activeElement === request.target);
      }

      restoreRequestFocus(request) {
        const target = request.targetId && document.getElementById(request.targetId);
        if (
          !target ||
          target.tagName !== request.target?.tagName ||
          target.dataset.optionValueId !== request.target?.dataset.optionValueId ||
          target.closest('product-info')?.dataset.section !== this.dataset.section
        ) {
          return;
        }

        target.focus({ preventScroll: true });
      }

      renderProductInfo({ requestUrl, target, targetId, callback, beforeCommit, onError }) {
        this.abortController?.abort();
        const controller = new AbortController();
        const variantSelectors = this.variantSelectors;
        const request = {
          controller,
          deferred: variantSelectors?.pendingSelectPromise,
          deferredSettled: false,
          target: target || (targetId ? document.getElementById(targetId) : null),
          targetId,
          variantSelectors,
        };
        this.abortController = controller;

        return fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (response.ok === false) throw new Error('Product response unavailable');
            return response.text();
          })
          .then(async (responseText) => {
            if (!this.isCurrentRequest(request)) {
              this.rejectRequestDeferred(request, this.createAbortError());
              return;
            }

            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (beforeCommit) await beforeCommit(html, request);
            if (!this.isCurrentRequest(request)) {
              this.rejectRequestDeferred(request, this.createAbortError());
              return;
            }
            const shouldRestoreFocus = this.hasRequestFocus(request);
            this.pendingRequestUrl = null;
            const result = callback(html, request);
            if (shouldRestoreFocus && this.isCurrentRequest(request)) this.restoreRequestFocus(request);
            return result;
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.rejectRequestDeferred(request, error);
            if (this.abortController === controller) onError?.(error);
            return false;
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const sourceVariantSelects = html.querySelector('variant-selects');
        const currentVariantSelects = this.variantSelectors;
        if (!sourceVariantSelects || !currentVariantSelects) return;

        if (this.hasMatchingOptionTopology(currentVariantSelects, sourceVariantSelects)) {
          this.reconcileOptionValues(currentVariantSelects, sourceVariantSelects);
          return;
        }

        this.prepareSizeChartEntry(sourceVariantSelects, html.querySelector('product-info'));
        HTMLUpdateUtility.viewTransition(currentVariantSelects, sourceVariantSelects, this.preProcessHtmlCallbacks);
      }

      hasMatchingOptionTopology(currentVariantSelects, sourceVariantSelects) {
        if (currentVariantSelects.dataset.productId !== sourceVariantSelects.dataset.productId) return false;

        const selector = 'input[data-option-value-id], option[data-option-value-id]';
        const currentOptions = Array.from(currentVariantSelects.querySelectorAll(selector));
        const sourceOptions = Array.from(sourceVariantSelects.querySelectorAll(selector));
        return (
          currentOptions.length === sourceOptions.length &&
          currentOptions.every(
            (option, index) =>
              option.tagName === sourceOptions[index].tagName &&
              option.dataset.optionValueId === sourceOptions[index].dataset.optionValueId
          )
        );
      }

      reconcileOptionValues(currentVariantSelects, sourceVariantSelects) {
        this.copyServerAttributes(currentVariantSelects, sourceVariantSelects);

        const selector = 'input[data-option-value-id], option[data-option-value-id]';
        const currentOptions = Array.from(currentVariantSelects.querySelectorAll(selector));
        const sourceOptions = Array.from(sourceVariantSelects.querySelectorAll(selector));
        sourceOptions.forEach((sourceOption, index) => {
          const currentOption = currentOptions[index];
          this.copyServerAttributes(currentOption, sourceOption);

          if (currentOption.tagName === 'INPUT') {
            currentOption.checked = sourceOption.checked;
          } else {
            currentOption.selected = sourceOption.selected;
            currentOption.textContent = sourceOption.textContent;
          }
        });

        const currentSelects = Array.from(currentVariantSelects.querySelectorAll('select'));
        const sourceSelects = Array.from(sourceVariantSelects.querySelectorAll('select'));
        sourceSelects.forEach((sourceSelect, index) => {
          const currentSelect = currentSelects[index];
          if (!currentSelect) return;
          this.copyServerAttributes(currentSelect, sourceSelect);
          currentSelect.value = sourceSelect.value;
        });

        const currentVariantScript = currentVariantSelects.querySelector('[data-selected-variant]');
        const sourceVariantScript = sourceVariantSelects.querySelector('[data-selected-variant]');
        if (currentVariantScript && sourceVariantScript) {
          this.copyServerAttributes(currentVariantScript, sourceVariantScript);
          currentVariantScript.textContent = sourceVariantScript.textContent;
        }
      }

      copyServerAttributes(destination, source) {
        Array.from(destination.attributes).forEach((attribute) => {
          if (!source.hasAttribute(attribute.name)) destination.removeAttribute(attribute.name);
        });
        Array.from(source.attributes).forEach((attribute) => {
          if (destination.getAttribute(attribute.name) !== attribute.value) {
            destination.setAttribute(attribute.name, attribute.value);
          }
        });
      }

      prepareSizeChartEntry(sourceVariantSelects, sourceProductInfo) {
        const currentEntry = this.variantSelectors?.querySelector('[data-z-size-chart-entry]');
        const sourceLabel = sourceVariantSelects?.querySelector('[data-zico-size-option-group] .form__label');
        if (!currentEntry || !sourceLabel || sourceLabel.querySelector('[data-z-size-chart-entry]')) return;

        const entry = currentEntry.cloneNode(true);
        const chart = sourceProductInfo?.querySelector('[data-zico-size-guide] [data-zico-chart-open]');
        const controls = chart?.getAttribute('aria-controls');
        if (controls) entry.setAttribute('aria-controls', controls);
        sourceLabel.append(entry);
      }

      handleUpdateProductInfo(productUrl) {
        return (html, request) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          this.resolveRequestDeferred(request, variant, sourceVariantSelects);

          const hasExplicitSize = this.hasExplicitSizeSelection();
          this.pickupAvailability?.update(hasExplicitSize ? variant : null);
          this.updateOptionValues(html);
          this.updateURL(productUrl, hasExplicitSize ? variant?.id : null);
          this.updateVariantInputs(hasExplicitSize ? variant?.id : null);

          if (!variant) {
            this.setUnavailable();
            this.enforceExplicitSizeSelection();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          this.enforceExplicitSizeSelection();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
      const currentVerified = this.querySelector('z-verified-gallery');
      const nextVerified = html.querySelector('z-verified-gallery');
      if (currentVerified || nextVerified) {
        const current = currentVerified || this.querySelector('media-gallery');
        const next = nextVerified || html.querySelector('media-gallery');
        if (current && next && (!currentVerified || !nextVerified || current.dataset.mediaKey !== next.dataset.mediaKey)) {
          current.replaceWith(next);
        }
        if (nextVerified) return;
      }
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { preventPageScroll: true }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
